
import React, { useState } from 'react';
import { Heart, MessageCircle, Share2, Clock, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { TimeLapse } from './TimeLapse';
import { CommentsSection } from './CommentsSection';
import { VideoResponses } from './VideoResponses';
import { PostModal } from './PostModal';
import { MentionText } from './MentionText';
import { ReportButton } from './ReportButton';
import { TagButton } from './TagButton';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useVideoAutoplay } from '@/hooks/useVideoAutoplay';
import { useVideoAnalytics } from '@/hooks/useVideoAnalytics';
import { useLiteMode } from '@/hooks/useLiteMode';
import { isFeatureEnabled } from '@/config/featureFlags';
import type { Post } from '@/hooks/posts/types';
import { SocialShareDialog } from './SocialShareDialog';
import SmartMediaPlayer from '@/components/media/SmartMediaPlayer';
import { FeedOptimizedAvatar } from './FeedOptimizedAvatar';
import { FeedOptimizedImage } from './FeedOptimizedImage';
import { extractPublicId, useMediaOptimization } from '@/utils/media';

interface PostCardProps {
  post: Post;
  allPosts: Post[];
  onLike: (id: string) => void;
  onComment: () => void;
}

export const PostCard = ({ post, allPosts, onLike, onComment }: PostCardProps) => {
  const [showComments, setShowComments] = useState(false);
  const [showTimeLapse, setShowTimeLapse] = useState(false);
  const [showVideoResponses, setShowVideoResponses] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSocialShare, setShowSocialShare] = useState(false);
  // Using MediaPlayer controls; external mute state not required
  const { logAction } = useAnalytics();
  const { isLiteMode } = useLiteMode();
  
  // Use autoplay hook for videos (disabled in lite mode)
  const { videoRef, containerRef, isPlaying, isIntersecting } = useVideoAutoplay({
    threshold: 0.6,
    rootMargin: '-50px',
    playOnIntersect: !isLiteMode, // Disable autoplay in lite mode
    pauseOnExit: true
  });

  // Initialize video analytics
  const videoAnalytics = useVideoAnalytics({
    postId: post.id,
    videoUrl: post.image || '',
    cropType: post.crop || 'Unknown',
    userName: post.user.name
  });

  // Track video events
  const handleVideoPlay = React.useCallback(async () => {
    if (videoRef.current) {
      try {
        await videoAnalytics.trackVideoPlay(isIntersecting);
        
        // Set up progress tracking
        const video = videoRef.current;
        const handleTimeUpdate = () => {
          try {
            if (video.duration && !isNaN(video.duration)) {
              videoAnalytics.trackVideoProgress(video.currentTime, video.duration);
            }
          } catch (error) {
            console.warn('Video progress tracking error:', error);
          }
        };
        
        const handleVideoEnd = () => {
          try {
            videoAnalytics.trackEngagement('full_watch');
          } catch (error) {
            console.warn('Video end tracking error:', error);
          } finally {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('ended', handleVideoEnd);
          }
        };

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleVideoEnd);
      } catch (error) {
        console.warn('Video play tracking error:', error);
      }
    }
  }, [videoAnalytics, isIntersecting]);

  const handleVideoPause = React.useCallback(async () => {
    if (videoRef.current) {
      try {
        await videoAnalytics.trackVideoPause(videoRef.current.currentTime);
      } catch (error) {
        console.warn('Video pause tracking error:', error);
      }
    }
  }, [videoAnalytics]);


  // Track intersection changes
  React.useEffect(() => {
    if (post.isVideo) {
      try {
        if (isIntersecting) {
          videoAnalytics.trackEngagement('intersect_start', { intersectionRatio: 0.6 });
        } else {
          videoAnalytics.trackEngagement('intersect_end');
        }
      } catch (error) {
        console.warn('Video intersection tracking error:', error);
      }
    }
  }, [isIntersecting, post.isVideo, videoAnalytics]);

  // Track video play/pause state changes
  React.useEffect(() => {
    if (post.isVideo && videoRef.current) {
      try {
        if (isPlaying) {
          handleVideoPlay();
        } else {
          handleVideoPause();
        }
      } catch (error) {
        console.warn('Video play/pause state tracking error:', error);
      }
    }
  }, [isPlaying, post.isVideo, handleVideoPlay, handleVideoPause]);

  const handleMentionClick = (username: string) => {
    console.log('Navigate to user profile:', username);
    // TODO: Navigate to user profile or show user info
  };

  const handleTimeLapseClick = () => {
    setShowTimeLapse(true);
    logAction('time_lapse_click', {
      post_id: post.id,
      crop_type: post.crop || 'Unknown',
      user_name: post.user.name
    });
  };

  const handleShareClick = () => {
    // Always show the social share dialog
    setShowSocialShare(true);
  };

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'great':
        return 'bg-gradient-to-r from-green-500 to-green-600';
      case 'help':
        return 'bg-gradient-to-r from-red-500 to-red-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
  };

  const getSentimentText = (sentiment?: string) => {
    switch (sentiment) {
      case 'great':
        return '🎉 Going Great';
      case 'help':
        return '🆘 Need Help';
      default:
        return '📈 Update';
    }
  };

  return (
    <div 
      ref={post.isVideo ? containerRef as React.RefObject<HTMLDivElement> : undefined}
      className={`rounded-xl border border-gray-200 overflow-hidden mb-6 bg-white ${
        isLiteMode 
          ? 'shadow-none p-2' 
          : 'shadow-sm p-0'
      } ${post.isVideo || post.image ? 'card-has-media' : ''}`}
    >
      {/* User Info */}
      <div className="p-4 pb-3">
        <div className="flex items-start space-x-3">
          <FeedOptimizedAvatar 
            src={post.user.avatar}
            alt={post.user.name}
            fallback={post.user.name.charAt(0)}
            className="w-10 h-10 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-gray-900 truncate">{post.user.name}</h3>
              {post.user.verified && (
                <span className="text-blue-500">✓</span>
              )}
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock size={12} />
                <span>{post.timestamp}</span>
                <span>•</span>
                <span className="text-green-600 font-medium">{post.crop}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <ReportButton
              contentId={post.id}
              contentType="post"
              contentPreview={post.content}
            />
          </div>
        </div>
      </div>

      {/* Post Content with Mentions */}
      <div className="px-4 pb-3">
        <MentionText 
          onMentionClick={handleMentionClick}
          className="text-gray-800 leading-relaxed"
        >
          {post.content}
        </MentionText>
        
        {post.whatsHappening && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-200">
            <p className="text-sm text-blue-800"><strong>What's happening:</strong> {post.whatsHappening}</p>
          </div>
        )}
        
        {post.challenges && (
          <div className="mt-3 p-3 bg-yellow-50 rounded-lg border-l-4 border-yellow-200">
            <p className="text-sm text-yellow-800"><strong>Challenges:</strong> {post.challenges}</p>
          </div>
        )}
      </div>

      {/* Media Section */}
      {post.image && (
        <div className="relative w-full">
          {post.isVideo ? (
            <SmartMediaPlayer
              mode={isLiteMode ? 'lite' : 'full'}
              provider={(post.cloud_public_id || extractPublicId(post.image)) ? 'cloudinary' : 'none'}
              cloudPublicId={(post.cloud_public_id || extractPublicId(post.image)) || undefined}
              posterPublicId={post.thumb_public_id || post.cloud_public_id || extractPublicId(post.image) || undefined}
              className="w-full h-40 sm:h-48 md:h-56 object-cover"
              autoOnVisible={!isLiteMode && isIntersecting}
              externalVideoRef={videoRef as React.RefObject<HTMLVideoElement>}
              onPlay={() => {
                try {
                  videoAnalytics.trackEngagement('scroll_to_view');
                } catch (error) {
                  console.warn('Video play tracking error:', error);
                }
              }}
              onError={() => {
                console.error('Video failed to load/play', post.image);
              }}
            />
          ) : (
            <>
              <FeedOptimizedImage 
                src={post.image} 
                alt="Post content"
                className="w-full h-40 sm:h-48 md:h-56 object-cover rounded-lg"
              />
              
              {/* Sentiment Badge - Top Right Overlay for images */}
              {post.sentiment && (
                <div className="absolute top-4 right-4 z-10">
                  <div className={`${getSentimentColor(post.sentiment)} text-white px-4 py-2 rounded-full text-sm font-bold shadow-xl border-2 border-white/20 whitespace-nowrap`}>
                    {getSentimentText(post.sentiment)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Prominent Time Lapse Button */}
      <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-blue-50 border-y border-green-100">
        <Button
          onClick={handleTimeLapseClick}
          className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02]"
          aria-label="View time lapse"
        >
          <Camera size={20} className="mr-2" />
          <span className="text-base">View {post.crop || 'Crop'} Time Lapse</span>
          <div className="ml-2 bg-white bg-opacity-20 rounded-full px-2 py-1 text-xs">
            Growth Journey
          </div>
        </Button>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
        <div className="flex items-center space-x-6">
          <button
            onClick={() => onLike(post.id)}
            className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
              post.isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
            }`}
            aria-label="Like post"
          >
            <Heart size={16} fill={post.isLiked ? 'currentColor' : 'none'} />
            <span>{post.likes}</span>
          </button>
          <button
            onClick={() => {
              setShowComments(!showComments);
              onComment();
            }}
            className="flex items-center space-x-1 text-sm text-gray-500 hover:text-blue-500 transition-colors"
            aria-label="Comment on post"
          >
            <MessageCircle size={16} />
            <span>{post.comments}</span>
          </button>
          <Button variant="ghost" size="sm" onClick={handleShareClick}>
            <Share2 size={16} />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <TagButton
            targetUserId={post.user_id || post.id}
            targetUserName={post.user.name}
            contentType="post"
            contentId={post.id}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVideoResponses(!showVideoResponses)}
            aria-label="Toggle video responses"
          >
            Responses
          </Button>
        </div>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <CommentsSection postId={post.id} isExpanded={true} />
        </div>
      )}

      {/* Video Responses Section */}
      {showVideoResponses && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <VideoResponses postId={post.id} farmName={post.user.name} />
        </div>
      )}

      {/* Time Lapse Modal */}
      {showTimeLapse && (
        <TimeLapse
          crop={post.crop || 'Unknown'}
          posts={allPosts}
          onClose={() => setShowTimeLapse(false)}
        />
      )}

      {/* Post Modal */}
      {showModal && (
        <PostModal
          post={post}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Social Share Modal */}
      {showSocialShare && (
        <SocialShareDialog
          post={post}
          open={showSocialShare}
          onOpenChange={setShowSocialShare}
        />
      )}
    </div>
  );
};
